import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Modal from '../../../../../../Common/Modal/Modal';
import { InputText } from 'primereact/inputtext';
import Toggle from '@Components/Common/Toggle/Toggle';
import { useSelector } from 'react-redux';

interface Phase {
	id: number;
	name: string;
	isActive: boolean;
	subscriptionId: number;
}

interface AddPhaseProps {
	onSave: (department: Phase) => void;
	onCancel: () => void;
}

const AddPhase: React.FC<AddPhaseProps> = ({ onSave, onCancel }) => {
	// Memoize subscription ID to prevent unnecessary re-renders
	const currentSubscriptionId = useSelector((state: { user: { currentSubscriptionId: number } }) => state.user.currentSubscriptionId);

	const initialPhase = useMemo(
		() => ({
			id: 0,
			name: '',
			isActive: true,
			subscriptionId: currentSubscriptionId || 0,
			disciplineId: 0,
		}),
		[currentSubscriptionId]
	);

	const [newPhase, setNewPhase] = useState<Phase>(initialPhase);
	const isInputInvalid = !newPhase.name.trim(); // Dynamically determine invalid state

	// Memoized change handlers
	const handleOnChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		setNewPhase((prev) => (prev.name === value ? prev : { ...prev, name: value }));
	}, []);

	const handleToggleChange = useCallback((e: { target: { checked: boolean } }) => {
		setNewPhase((prev) => (prev.isActive === e.target.checked ? prev : { ...prev, isActive: e.target.checked }));
	}, []);

	const handleSave = useCallback(() => {
		if (isInputInvalid) return;
		onSave(newPhase);
	}, [isInputInvalid, onSave, newPhase]);

	return (
		<Modal
			title='Add Phase'
			body={
				<>
					<InputText
						id='name'
						name='name'
						placeholder='Name'
						invalid={isInputInvalid}
						type='text'
						value={newPhase.name}
						onChange={handleOnChange}
						maxLength={175}
						className='mb-3'
					/>
					<Toggle name='isActive' text='Active' checked={newPhase.isActive} onChange={handleToggleChange} />
				</>
			}
			primaryButtonText='Add'
			onPrimaryClick={handleSave}
			onSecondaryClick={onCancel}
			onCloseClick={onCancel}
		/>
	);
};

AddPhase.propTypes = {
	onSave: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
};

export default AddPhase;
